import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService, ConfigStateService } from '@abp/ng.core';
import { SommelierService } from '@proxy/sommeliers';
import { ClientService } from '@proxy/clients';
import { Observable, of, switchMap, map, catchError, filter, take } from 'rxjs';

/**
 * Guard de enrutamiento que decide el destino inicial cuando el usuario
 * navega a la raíz ('').
 *
 * Comportamiento:
 * - Si NO está autenticado -> permite cargar Home normalmente.
 * - Si está autenticado -> espera a que `currentUser.roles` esté poblado.
 *   - Si los roles todavía no llegaron, fuerza `refreshAppState()` para
 *     traer la configuración de ABP y reintenta hasta tener roles.
 *   - Con roles presentes, resuelve el slug según el rol y redirige a `/${slug}`
 *     mediante UrlTree (sin renderizar Home).
 * - Si no hay slug disponible -> permite continuar (no bloquea la navegación).
 */
export const routeRedirectCanActivate: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const config = inject(ConfigStateService);
  const router = inject(Router);
  const sommelierService = inject(SommelierService);
  const clientService = inject(ClientService);

  // Usuario anónimo: no interceptar, dejar que Home cargue.
  if (!auth.isAuthenticated) {
    return of(true);
  }

  return config.getOne$('currentUser').pipe(
    // Espera a que haya un usuario (puede estar sin roles aún tras el login)
    filter(user => !!user),
    take(1),
    switchMap(user => {
      const roles = (user as any).roles as string[] | undefined;

      // Caso post-login inmediato: autenticado pero sin roles cargados todavía.
      // Forzamos la carga de la Application Configuration y reintentamos hasta obtener roles.
      if (auth.isAuthenticated && (!roles || roles.length === 0)) {
        return config.refreshAppState().pipe(
          switchMap(() => config.getOne$('currentUser').pipe(
            filter(u => !!u && Array.isArray((u as any).roles) && (u as any).roles.length > 0),
            take(1)
          ))
        );
      }

      // Ya hay roles: continuamos con el usuario actual.
      return of(user as any);
    }),
    // Resolver el slug según el rol efectivo del usuario.
    switchMap(userWithRoles => {
      const roles = (userWithRoles as any)?.roles ?? [];
      if (roles.includes('sommelier')) {
        return sommelierService.getMySlug();
      }
      if (roles.includes('client')) {
        return clientService.getSommelierSlug();
      }
      // Otros roles o sin rol relevante -> no redirigimos.
      return of(undefined as unknown as string);
    }),
    // Si hay slug, devolvemos UrlTree a '/{slug}' para redirigir antes de renderizar Home.
    map(slug => {
      if (slug) {
        return router.createUrlTree([`/${slug}`]);
      }
      // Sin slug -> permitimos la navegación a Home.
      return true;
    }),
    // Ante cualquier error de red, backend, etc., degradamos permitiendo Home.
    catchError(() => of(true))
  );
};


